import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import CalendarDatePicker from './CalendarDatePicker';
import { wallClockPartsInTz } from '@/lib/facilityTimezone';

/**
 * DateTimePicker — a scheduled-send control: the site's calendar picker with an hour /
 * minute / AM-PM row inside its panel, one field, one label, one error.
 *
 * It replaces `<input type="datetime-local">`, whose picker walks the user through a
 * date, then an hour wheel, then a minute wheel, and which on most engines lets a past
 * slot through unless the caller both sets `min` and re-checks it. Here `min` is
 * enforced at the source: days before it cannot be picked or typed, an AM/PM period
 * that is wholly behind the floor on that day is greyed out, and a time that still
 * lands before the floor is flagged in red with the message the email editor already
 * used for the same case.
 *
 * The time defaults to the clock at the moment the panel opens, so picking a day is
 * enough to schedule; the row is there to adjust it. Nothing is applied until a day is
 * chosen: a time set on its own is kept as the time to use, and reported the moment a
 * date completes the pair. Clearing the date drops the time with it.
 *
 * Value contract is the native control's: 'YYYY-MM-DDTHH:mm' in whatever wall-clock the
 * caller means (the editors mean the user's own timezone), or '' when unset. Callers
 * that were storing the native value keep working unchanged.
 */

const DATETIME_RE = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/;

export function splitDateTimeValue(value: string | null | undefined): { date: string; time: string } {
  const match = value ? DATETIME_RE.exec(value) : null;
  if (!match) return { date: '', time: '' };
  return { date: match[1], time: match[2] };
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * A Date's LOCAL wall-clock as 'YYYY-MM-DDTHH:mm'. Never `toISOString().slice(0, 16)`
 * for this: that is the UTC wall-clock, and it showed campaigns scheduled for 9 pm as
 * 4 am to anyone east of UTC and the previous evening to anyone west.
 */
export function toDateTimeValue(date: Date): string {
  if (isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/**
 * The current minute as 'YYYY-MM-DDTHH:mm', in `tz` when given, else the browser's zone.
 * A `tz` that can't be read also falls back to the browser's zone.
 */
export function nowAsDateTimeValue(tz?: string): string {
  const now = new Date();
  const p = tz ? wallClockPartsInTz(now, tz) : null;
  if (!p) return toDateTimeValue(now);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

export const PAST_TIME_MESSAGE = 'That time has already passed. Pick a later one.';

export interface DateTimePickerProps {
  /** 'YYYY-MM-DDTHH:mm', or '' when unset. */
  value: string;
  onChange: (value: string) => void;
  /** Earliest allowed 'YYYY-MM-DDTHH:mm'. Usually `nowAsDateTimeValue(userTz)`. */
  min?: string;
  /** Latest allowed 'YYYY-MM-DDTHH:mm'. */
  max?: string;
  label?: string;
  hint?: string;
  /** Overrides the automatic past-time message when given. */
  error?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  /** Accessible name when no `label` is rendered. */
  ariaLabel?: string;
  placeholder?: string;
  /** Classes for the bordered field box. */
  className?: string;
  wrapperClassName?: string;
}

export function DateTimePicker({
  value,
  onChange,
  min,
  max,
  label,
  hint,
  error,
  required,
  disabled,
  id,
  ariaLabel,
  placeholder,
  className,
  wrapperClassName,
}: DateTimePickerProps) {
  const reactId = useId();
  const inputId = id ?? `datetime-${reactId}`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  // `time` may be set while `date` is not: that is the time waiting for a date. It is
  // never reported upward on its own.
  const [parts, setParts] = useState(() => splitDateTimeValue(value));
  const partsRef = useRef(parts);

  // Adopt outside changes without clobbering a time that is waiting for its date: only
  // re-derive when the committed value differs from ours.
  const committedRef = useRef(value);
  useEffect(() => {
    if (committedRef.current !== value) {
      committedRef.current = value;
      const next = splitDateTimeValue(value);
      partsRef.current = next;
      setParts(next);
    }
  }, [value]);

  function update(patch: Partial<{ date: string; time: string }>) {
    // The calendar reports the default time and then the date in two calls within the
    // same tick, so compose from a ref rather than from the last rendered state.
    const next = { ...partsRef.current, ...patch };
    partsRef.current = next;
    setParts(next);
    const combined = next.date && next.time ? `${next.date}T${next.time}` : '';
    if (combined !== committedRef.current) {
      committedRef.current = combined;
      onChange(combined);
    }
  }

  const minParts = splitDateTimeValue(min);
  const maxParts = splitDateTimeValue(max);
  const timeMin = minParts.date && parts.date === minParts.date ? minParts.time : undefined;

  const inPast = !!value && !!min && value < min;
  const beyondMax = !!value && !!max && value > max;
  const shownError = error ?? (inPast ? PAST_TIME_MESSAGE : beyondMax ? 'That time is later than allowed.' : undefined);

  return (
    <div className={cn('w-full', wrapperClassName)}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && (
            <span className="ml-0.5 text-red-500" aria-hidden="true">
              {' *'}
            </span>
          )}
        </label>
      )}

      <CalendarDatePicker
        id={inputId}
        value={parts.date}
        onChange={(date) => update({ date })}
        time={parts.time}
        onTimeChange={(time) => update({ time })}
        timeMin={timeMin}
        min={minParts.date || undefined}
        max={maxParts.date || undefined}
        disabled={disabled}
        required={required}
        invalid={!!shownError}
        clearable
        placeholder={placeholder}
        ariaLabel={label ? undefined : ariaLabel}
        ariaDescribedBy={shownError ? errorId : hint ? hintId : undefined}
        className={className}
      />

      {hint && !shownError && (
        <p id={hintId} className="mt-1.5 text-xs text-slate-500">
          {hint}
        </p>
      )}
      {shownError && (
        <p id={errorId} role="alert" className="mt-1.5 text-xs text-red-600">
          {shownError}
        </p>
      )}
    </div>
  );
}

DateTimePicker.displayName = 'DateTimePicker';
